/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
const promptEditorWrapperStyles = css({
  display: 'grid',
  position: 'relative',
  flex: 1,
  'div > .ProseMirror': {
    '> p:last-child': {
      marginBottom: 0,
      paddingBottom: 0
    },
    /**
     * Minimum height is set to 125px for confluence inline comment editor (ProseMirror element)
     * When Editor AI is invoked in inline comments, same css rule applies here as well.
     * So overriding it here.
     */
    minHeight: 'auto'
  },
  // Smart link's inline card view has 0.5em padding (7px or 8px)
  // Because of that scrollbar appearts in prompt input because of limited height available.
  // More details here https://product-fabric.atlassian.net/browse/EDF-455
  // So removed this top and bottom padding here.
  '.ProseMirror .inlineCardView-content-wrap .card': {
    paddingTop: 0,
    paddingBottom: 0
  }
});

// The types PromptEditorProps and PromptEditor should mirror what is in the editor-ai-injected-editors package
// at `packages/editor/editor-ai-injected-editors/src/editors/prompt.tsx`.

export const PromptEditorWrapper = ({
  PromptEditor,
  defaultValue,
  autoFocus,
  focusInputRef,
  blurInputRef,
  clearInputRef,
  placeholder,
  enableLinks,
  onInputChange,
  onADFChange,
  onBlur,
  onFocus,
  onKeyDown,
  onCompositionStart,
  onCompositionEnd
}) => {
  const setFocusHandlers = useCallback((blur, focus) => {
    if (blurInputRef) {
      blurInputRef.current = blur;
    }
    if (focusInputRef) {
      focusInputRef.current = focus;
    }
  }, [blurInputRef, focusInputRef]);
  const setClearHandler = useCallback(clear => {
    if (clearInputRef) {
      clearInputRef.current = clear;
    }
  }, [clearInputRef]);
  const componentId = useMemo(() => uuidv4(), []);
  return (
    // eslint-disable-next-line @atlaskit/design-system/prefer-primitives
    jsx("div", {
      css: promptEditorWrapperStyles,
      "data-testid": "ai-experience-prompt-input",
      onKeyDown: onKeyDown
      // We're using this div to listen to focus/blur/composition events which bubble up.
      ,
      onFocus: onFocus,
      onBlur: onBlur,
      onCompositionStart: onCompositionStart,
      onCompositionEnd: onCompositionEnd
    }, jsx(PromptEditor, {
      key: `${placeholder}-${componentId}`,
      autoFocus: autoFocus,
      defaultValue: defaultValue,
      placeholder: placeholder,
      onInputChange: onInputChange,
      onADFChange: onADFChange,
      setFocusHandlers: setFocusHandlers,
      setClearHandler: setClearHandler,
      enableLinks: enableLinks
    }))
  );
};